/**
 * WebSocketContext - Shared WebSocket Connection
 *
 * Context นี้จัดการ WebSocket connection เพียง 1 connection ต่อ session
 * และให้ consumer (NotificationContext, MaintenanceAlertContext) subscribe
 * รับ message แทนการสร้าง connection แยกกัน
 */

import React, {
  createContext,
  useContext,
  useCallback,
  useRef,
} from 'react';
import useWebSocket, { UseWebSocketReturn, WebSocketMessage } from '../hooks/useWebSocket';
import { useMaintenanceAlertSettings } from '../hooks/useMaintenanceAlertSettings';

// =============== Types ===============

export interface WebSocketContextType {
  isConnected: boolean;
  isConnecting: boolean;
  connectionError: string | null;
  connect: () => void;
  disconnect: () => void;
  sendMessage: (message: WebSocketMessage) => void;
  /**
   * Subscribe to receive all application-level WebSocket messages.
   * Returns an unsubscribe function — call it in the useEffect cleanup.
   *
   * @example
   * useEffect(() => {
   *   return subscribe((msg) => { ... });
   * }, [subscribe]);
   */
  subscribe: (handler: (message: any) => void) => () => void;
}

// =============== Context ===============

const WebSocketContext = createContext<WebSocketContextType | null>(null);

// =============== Provider ===============

interface WebSocketProviderProps {
  children: React.ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  // Store all active message handlers in a Set via ref (avoids re-renders on subscribe/unsubscribe)
  const handlersRef = useRef<Set<(msg: any) => void>>(new Set());

  // Stable onMessage callback that broadcasts to all subscribers
  // Uses useCallback with empty deps so the reference never changes — prevents reconnect loops
  const handleMessage = useCallback((message: any) => {
    handlersRef.current.forEach((handler) => {
      try {
        handler(message);
      } catch (err) {
        console.error('WebSocketContext: handler threw an error', err);
      }
    });
  }, []); // stable reference — intentionally empty deps

  // S1133 FIX (OBS-S1133-001): wire user's autoConnect setting through to useWebSocket.
  // Previously autoConnect was hardcoded `true` — the toggle in NotificationSettingsPage
  // persisted to localStorage but had no effect on actual connection behavior.
  //
  // useMaintenanceAlertSettings reads localStorage in an effect, so on first render `settings`
  // is the in-code defaults (autoConnect: true). We gate with `isLoaded` to avoid a premature
  // connect→disconnect bounce when the user's stored preference is `false`.
  // useWebSocket already supports dynamic autoConnect transitions
  // (true→false disconnects, false→true reconnects) so the wire-up is safe.
  const { settings: alertSettings, isLoaded: settingsLoaded } = useMaintenanceAlertSettings();
  const effectiveAutoConnect = settingsLoaded ? alertSettings.autoConnect : false;

  const {
    isConnected,
    isConnecting,
    connectionError,
    connect,
    disconnect,
    sendMessage,
  }: UseWebSocketReturn = useWebSocket({
    autoConnect: effectiveAutoConnect,
    onMessage: handleMessage,
  });

  // Subscribe a handler — returns the unsubscribe function
  const subscribe = useCallback((handler: (msg: any) => void): (() => void) => {
    handlersRef.current.add(handler);
    return () => {
      handlersRef.current.delete(handler);
    };
  }, []); // stable reference

  const contextValue: WebSocketContextType = {
    isConnected,
    isConnecting,
    connectionError,
    connect,
    disconnect,
    sendMessage,
    subscribe,
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

// =============== Hook ===============

/**
 * useWebSocketContext — consumes the shared WebSocket connection.
 * Must be used inside <WebSocketProvider>.
 *
 * BUG-S1146-001 FIX (continuation of BUG-S1144-001 useAuth defensive pattern):
 * Returns safe no-op defaults if context is unavailable (Vite HMR race or rare
 * lazy-load timing) instead of throwing. Prevents ErrorBoundary trigger.
 * Logs warning loudly so genuine "forgot WebSocketProvider" bugs remain visible.
 */
const WS_FALLBACK_DEFAULTS: WebSocketContextType = {
  isConnected: false,
  isConnecting: false,
  connectionError: 'WebSocket context unavailable',
  connect: () => {},
  disconnect: () => {},
  sendMessage: () => {},
  subscribe: () => () => {},
};

export const useWebSocketContext = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (context === null) {
    if (typeof console !== 'undefined') {
      console.warn(
        '[useWebSocketContext] WebSocketContext unavailable — using safe defaults. ' +
        'Likely Vite HMR race. If seen in production, ensure <WebSocketProvider> wraps the tree.'
      );
    }
    return WS_FALLBACK_DEFAULTS;
  }
  return context;
};

export default WebSocketContext;
